import api from './api';

export const transactionService = {
  /**
   * Utwórz nową transakcję (koszyk) używając nowego API POS
   * @param {Object} transactionData - Dane transakcji
   * @returns {Promise} Utworzona transakcja
   */
  async createTransaction(transactionData) {
    try {
      // 1. Utwórz nowy koszyk
      const cartResponse = await api.post('/pos/cart/new', {
        kasjer_id: transactionData.cashier,
        location_id: transactionData.location_id
      });
      
      if (!cartResponse.data.success) {
        throw new Error(cartResponse.data.message || 'Błąd tworzenia koszyka');
      }
      
      const transactionId = cartResponse.data.data?.transakcja_id || cartResponse.data.message?.transakcja_id;
      
      // 2. Dodaj produkty do koszyka (z ceną z frontendu!)
      for (const item of transactionData.items) {
        const itemResponse = await api.post(`/pos/cart/${transactionId}/items`, {
          product_id: item.product_id,
          ilosc: item.quantity,
          cena_jednostkowa: item.price  // WAŻNE: wysyłaj cenę z koszyka (może być edytowana)
        });

        if (!itemResponse.data.success) {
          throw new Error(`Błąd dodawania produktu: ${itemResponse.data.message}`);
        }
      }
      
      // 3. Dodaj klienta jeśli podany - używamy finalizacji z customer_id
      // Klient zostanie dodany w kroku finalizacji

      // USUNIETO (2026-08): martwa galaz "status === 'zakonczony'", ktora
      // finalizowala transakcje od razu przy tworzeniu, BEZ discount_amount
      // i bez pelnych danych platnosci. Zaden wolajacy nie ustawial
      // status='zakonczony' (usePaymentProcessing.buildTransactionData nie ma
      // pola status), a jej ciche ozywienie zmieniloby zrodlo kwoty rabatu.
      // Finalizacja idzie WYLACZNIE przez completeTransaction ponizej
      // (POST /pos/cart/<id>/finalize z discount_amount liczonym silnikiem).

      return {
        success: true,
        data: {
          transaction_id: transactionId,
          status: transactionData.status || 'w_trakcie'
        }
      };
      
    } catch (error) {
      console.error('Błąd tworzenia transakcji:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  /**
   * Pobierz szczegóły transakcji/koszyka używając nowego API POS
   * @param {number} transactionId - ID transakcji
   * @returns {Promise} Dane transakcji
   */
  async getTransaction(transactionId) {
    try {
      const response = await api.get(`/pos/cart/${transactionId}`);
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Błąd pobierania transakcji');
      }
      
      // Mapuj dane z API na strukturę oczekiwaną przez frontend
      // API zwraca dane w response.data.data (nie response.data.message)
      const transactionData = response.data.data;
      
      // Zabezpieczenie gdy transakcja nie istnieje
      if (!transactionData || !transactionData.transakcja) {
        throw new Error('Transakcja nie została znaleziona');
      }
      
      return {
        success: true,
        data: {
          ...transactionData.transakcja,
          receipt_number: transactionData.transakcja.numer_paragonu,
          transaction_date: transactionData.transakcja.data_transakcji,
          transaction_time: transactionData.transakcja.czas_transakcji,
          cashier: transactionData.transakcja.kasjer_login,
          items: transactionData.pozycje || [],
          discounts: transactionData.rabaty || []
        }
      };
    } catch (error) {
      console.error('Błąd pobierania transakcji:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  /**
   * Dodaj lub usuń produkt z koszyka używając nowego API POS
   * @param {number} transactionId - ID transakcji
   * @param {Object} updateData - Dane do aktualizacji {action: 'add'|'remove', product_id, quantity, position_id}
   * @returns {Promise} Zaktualizowana transakcja
   */
  async updateTransaction(transactionId, updateData) {
    try {
      if (updateData.action === 'add') {
        // Dodaj nowy produkt do koszyka
        const response = await api.post(`/pos/cart/${transactionId}/items`, {
          product_id: updateData.product_id,
          ilosc: updateData.quantity || 1
        });
        
        if (!response.data.success) {
          throw new Error(response.data.message || 'Błąd dodawania produktu');
        }
        
        return {
          success: true,
          data: response.data
        };
        
      } else if (updateData.action === 'remove') {
        // Usuń pozycję z koszyka
        const response = await api.delete(`/pos/cart/${transactionId}/items/${updateData.position_id}`);
        
        if (!response.data.success) {
          throw new Error(response.data.message || 'Błąd usuwania pozycji');
        }
        
        return {
          success: true,
          data: response.data
        };
        
      } else if (updateData.action === 'update') {
        // Aktualizuj ilość pozycji
        const response = await api.put(`/pos/cart/${transactionId}/items/${updateData.position_id}`, {
          ilosc: updateData.quantity
        });
        
        if (!response.data.success) {
          throw new Error(response.data.message || 'Błąd aktualizacji pozycji');
        }
        
        return {
          success: true,
          data: response.data
        };
      }
      
      throw new Error('Nieznana akcja: ' + updateData.action);
      
    } catch (error) {
      console.error('Błąd aktualizacji transakcji:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  /**
   * Finalizuj transakcję (płatność) używając nowego API POS z automatycznym odejmowaniem stanów
   * @param {number} transactionId - ID transakcji
   * @param {Object} paymentData - Dane płatności {payment_method, customer_id, document_type}
   * @returns {Promise} Zakończona transakcja
   */
  async completeTransaction(transactionId, paymentData) {
    try {
      const requestBody = {
        payment_method: paymentData.payment_method || 'gotowka',
        customer_id: paymentData.customer_id || null,
        document_type: paymentData.document_type || 'paragon', // 'paragon', 'faktura_imienna', 'faktura_firma'
        kwota_otrzymana: paymentData.kwota_otrzymana || paymentData.amount_paid,
        amount_change: paymentData.amount_change || 0,
        notatka: paymentData.note || '',
        skip_wz: paymentData.skip_wz || false
      };

      // Dodaj discount_amount (rabat z uwzględnieniem wykluczeń)
      if (paymentData.discount_amount !== undefined) {
        requestBody.discount_amount = paymentData.discount_amount;
      }

      // Dodaj excluded_from_discount (ID produktów wykluczonych z rabatu)
      if (paymentData.excluded_from_discount && paymentData.excluded_from_discount.length > 0) {
        requestBody.excluded_from_discount = paymentData.excluded_from_discount;
      }

      // Dodaj split_payments jeśli istnieją (płatność dzielona)
      if (paymentData.split_payments && paymentData.split_payments.length > 0) {
        requestBody.split_payments = paymentData.split_payments;
        requestBody.payment_method = 'dzielona'; // Nadpisz metodę na "dzielona"
      }

      // Dodaj coupon_code jeśli istnieje
      if (paymentData.coupon_code) {
        requestBody.coupon_code = paymentData.coupon_code;
      }
      
      const response = await api.post(`/pos/cart/${transactionId}/finalize`, requestBody);

      return {
        success: response.data.success !== false,
        data: response.data,
        message: response.data.message,
        error: response.data.error
      };
    } catch (error) {
      console.error('Błąd finalizacji transakcji:', error);
      return {
        success: false,
        error: error.response?.data?.error || error.message,
        // Ustrukturyzowany blad finalizacji (kontrakt Z2): przy odmowie
        // serwer odsyla error_code (RABAT_REGULA_GROSZA / PLATNOSC_NIEZGODNA)
        // oraz kwoty poprawne (rabat_poprawny, do_zaplaty). Przekazujemy je
        // w calosci, zeby kasa mogla sie naprawic zamiast krecic sie w petli
        // 400 bez wyjscia (ustalenie D wlasciciela).
        errorData: error.response?.data || null
      };
    }
  },

  /**
   * Pobierz niezakończone transakcje (koszyki) używając nowego API POS
   * @param {string} cashier - Kasjer (opcjonalnie)
   * @param {number} limit - Limit wyników
   * @returns {Promise} Lista roboczych transakcji
   */
  async getDrafts(cashier = null, limit = 20) {
    try {
      const params = { limit };
      if (cashier) params.kasjer_id = cashier;
      
      const response = await api.get('/pos/carts', { params });
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Błąd pobierania koszyków');
      }
      
      return {
        success: true,
        data: response.data.carts || []
      };
    } catch (error) {
      console.error('Błąd pobierania roboczych transakcji:', error);
      return {
        success: false,
        data: [],
        error: error.message
      };
    }
  },

  /**
   * Pobierz statystyki dzienne
   * @param {number} locationId - ID lokalizacji (opcjonalne)
   * @returns {Promise} Statystyki dnia
   */
  async getDailyStats(locationId = null) {
    try {
      const url = locationId ? `/pos/stats?location_id=${locationId}` : '/pos/stats';
      const response = await api.get(url);
      return response.data;
    } catch (error) {
      console.error('Błąd pobierania statystyk dziennych:', error);
      return {
        success: false,
        data: {
          transactions_count: 0,
          total_revenue: 0,
          average_transaction: 0,
          today_average_transaction: 0,
          month_average_transaction: 0
        }
      };
    }
  },

  /**
   * Pobierz cel sprzedaży dla lokalizacji
   * @param {number} locationId - ID lokalizacji
   * @returns {Promise} Cel sprzedaży z postępem
   */
  async getSalesTarget(locationId) {
    try {
      const response = await api.get(`/pos/sales-target?location_id=${locationId}`);
      return response.data;
    } catch (error) {
      console.error('Błąd pobierania celu sprzedaży:', error);
      return {
        success: false,
        data: {
          target_amount: 0,
          current_revenue: 0,
          remaining_amount: 0,
          progress_percentage: 0,
          has_target: false
        }
      };
    }
  },

  /**
   * Pobierz statystyki miesięczne
   * @param {number|string} locationId - ID lokalizacji (opcjonalne)
   * @returns {Promise} Statystyki miesięczne
   */
  async getMonthlyStats(locationId = null) {
    try {
      const url = locationId ? `/pos/monthly-stats?location_id=${locationId}` : '/pos/monthly-stats';
      const response = await api.get(url);
      return response.data;
    } catch (error) {
      console.error('Błąd pobierania statystyk miesięcznych:', error);
      return {
        success: false,
        data: []
      };
    }
  },

  /**
   * Pobierz listę transakcji/paragonów używając nowego API POS
   * @param {Object} filters - Filtry (limit, status, date_from, date_to, cashier)
   * @returns {Promise} Lista transakcji
   */
  async getTransactions(filters = {}) {
    try {
      const params = {
        limit: filters.limit || 20,
        status: filters.status || 'completed',
        ...filters
      };
      
      // Używamy nowego endpointu POS dla listy transakcji
      const response = await api.get('/pos/transactions', { params });
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Błąd pobierania transakcji');
      }
      
      // Mapuj pola z API na oczekiwane nazwy w frontend
      const rawTransactions = response.data.data?.transactions || response.data.transactions || [];
      const mappedTransactions = rawTransactions.map(transaction => {
        // Fiskalizacja: 'ND' = korekta/nie dotyczy, 'F' = OK (1), 'F!' = blad (-1), 'NF' = nie fiskalizowany (0/null)
        let fiscal_status;
        if (transaction.fiscal_status === 'ND') {
          fiscal_status = 'ND';
        } else {
          const f = Number(transaction.fiskalizacja);
          if (f === 1) fiscal_status = 'F';
          else if (f === -1) fiscal_status = 'F!';
          else fiscal_status = 'NF';
        }
        return {
          ...transaction,
          receipt_number: transaction.numer_paragonu,
          transaction_date: transaction.data_transakcji || transaction.created_at,
          transaction_time: transaction.czas_transakcji || (transaction.created_at?.split('T')[1]?.split('.')[0]) || '',
          cashier: transaction.kasjer_id,
          items_count: transaction.items_count || 0,
          fiscal_status
        };
      });
      
      return {
        success: true,
        data: {
          transactions: mappedTransactions,
          total: response.data.data?.total || response.data.total || 0,
          limit: response.data.data?.limit || response.data.limit || 20
        }
      };
    } catch (error) {
      console.error('Błąd pobierania transakcji:', error);
      return {
        success: false,
        data: [],
        error: error.message
      };
    }
  },

  /**
   * Usuń transakcję/koszyk używając nowego API POS (tylko szkice i w toku)
   * @param {number} transactionId - ID transakcji
   * @returns {Promise} Wynik operacji
   */
  async deleteTransaction(transactionId) {
    try {
      const response = await api.delete(`/pos/cart/${transactionId}`);
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Błąd usuwania koszyka');
      }
      
      return {
        success: true,
        message: response.data.message
      };
    } catch (error) {
      console.error('Błąd usuwania transakcji:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  /**
   * Zmień status transakcji/koszyka używając nowego API POS
   * @param {number} transactionId - ID transakcji
   * @param {string} status - Nowy status (draft|w_trakcie)
   * @returns {Promise} Wynik operacji
   */
  async updateTransactionStatus(transactionId, status) {
    try {
      const response = await api.put(`/pos/cart/${transactionId}/status`, {
        status
      });

      if (!response.data.success) {
        throw new Error(response.data.message || 'Błąd zmiany statusu');
      }

      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Błąd zmiany statusu transakcji:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  /**
   * Pobierz szczegóły transakcji z pozycjami (dla fiskalizacji)
   * @param {number} transactionId - ID transakcji
   * @returns {Promise} Dane transakcji z pozycjami
   */
  async getTransactionDetails(transactionId) {
    try {
      const response = await api.get(`/pos/cart/${transactionId}`);

      if (!response.data.success) {
        throw new Error(response.data.message || 'Błąd pobierania szczegółów transakcji');
      }

      const transactionData = response.data.data;

      if (!transactionData || !transactionData.transakcja) {
        throw new Error('Transakcja nie została znaleziona');
      }

      return {
        success: true,
        data: {
          ...transactionData.transakcja,
          items: transactionData.pozycje || [],
          rabaty: transactionData.rabaty || []
        }
      };
    } catch (error) {
      console.error('Błąd pobierania szczegółów transakcji:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  /**
   * Aktualizuj status fiskalizacji transakcji
   * @param {number} transactionId - ID transakcji
   * @param {boolean} success - Czy fiskalizacja się powiodła
   * @returns {Promise} Wynik operacji
   */
  async updateFiscalStatus(transactionId, success, blad) {
    try {
      const response = await api.put(`/transactions/${transactionId}/fiscal-status`, {
        fiskalizacja: success ? 1 : -1,  // 1 = sukces, -1 = nieudana
        data_fiskalizacji: new Date().toISOString(),
        // Opis bledu - tylko przy fail (przy success backend i tak czysci)
        blad: !success && blad ? String(blad).substring(0, 500) : undefined,
      });

      return {
        success: response.data.success !== false,
        data: response.data
      };
    } catch (error) {
      console.error('Błąd aktualizacji statusu fiskalizacji:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  /**
   * Pobierz raporty marży netto ze sprzedaży (dzienny i miesięczny)
   * @param {number} locationId - ID lokalizacji (opcjonalne)
   * @returns {Promise} Raporty marży
   */
  async getMarginReports(locationId = null) {
    try {
      const url = locationId ? `/pos/margin-reports?location_id=${locationId}` : '/pos/margin-reports';
      const response = await api.get(url);
      return response.data;
    } catch (error) {
      console.error('Błąd pobierania raportów marży:', error);
      return {
        success: false,
        data: {
          daily: {},
          monthly: [],
          daily_history: [],
          summary: {}
        }
      };
    }
  },

  /**
   * Pobierz raport marży dla wybranego zakresu dat
   * @param {string} dateFrom - Data początkowa (YYYY-MM-DD)
   * @param {string} dateTo - Data końcowa (YYYY-MM-DD)
   * @param {number} locationId - ID lokalizacji (opcjonalne)
   * @returns {Promise} Raport marży dla zakresu
   */
  async getMarginReportsRange(dateFrom, dateTo, locationId = null) {
    try {
      let url = `/pos/margin-reports/range?date_from=${dateFrom}&date_to=${dateTo}`;
      if (locationId) {
        url += `&location_id=${locationId}`;
      }
      const response = await api.get(url);
      return response.data;
    } catch (error) {
      console.error('Błąd pobierania raportu marży dla zakresu:', error);
      return {
        success: false,
        data: {
          daily_data: [],
          summary: {}
        }
      };
    }
  }
};
